import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { getSupplierMaterials } from "@/apis/supplier";
import type { SupplierMaterialListItem } from "@/types/apis";

// ==================== 类型定义 ====================
export interface MaterialSelectOption {
  label: string;
  value: string;
}

export interface MaterialItem {
  id?: string | number;
  materialCode: string;
  materialName: string;
  materialStatus: string;
  materialType: string;
  supplierUserId: string;
}
// ==================== 自定义Hook ====================
/**
 * 物料数据查询Hook
 * 用于处理物料下拉框的模糊搜索和数据加载
 * 支持按供应商ID和物料编号进行查询
 */
export const useMaterialData = () => {
  const { t } = useTranslation("supplierRiskLevel");
  const [materialOptions, setMaterialOptions] = useState<
    MaterialSelectOption[]
  >([]);
  const [materialDataMap, setMaterialDataMap] = useState<
    Record<string, MaterialItem>
  >({});
  const [loading, setLoading] = useState(false);

  // 查询物料数据 - 支持supplierUserId参数
  const fetchMaterialData = useCallback(
    async (searchKey: string = "", supplierUserId?: string | number) => {
      try {
        setLoading(true);
        const response = await getSupplierMaterials({
          supplierUserId: supplierUserId,
          materialCode: searchKey,
        });

        const materialList = response.data?.data || [];

        // 过滤掉 materialCode 为空的项，避免下拉框出现空白行和重复key警告
        const validMaterialList = materialList.filter(
          (item: SupplierMaterialListItem) =>
            item.materialCode && item.materialCode.trim(),
        );

        const options: MaterialSelectOption[] = validMaterialList.map(
          (item: SupplierMaterialListItem) => ({
            label: `${item.materialCode}`,
            value: item.materialCode,
            key: `${item.id || ""}`,
          }),
        );
        setMaterialOptions(options);

        const dataMap: Record<string, MaterialItem> = {};
        validMaterialList.forEach((item) => {
          dataMap[item.materialCode] = item;
        });
        setMaterialDataMap(dataMap);
      } catch (error) {
        console.error("获取物料数据失败:", error);
        message.error(t("获取物料数据失败"));
        setMaterialOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  // 根据供应商编号获取供应商详细信息
  const getMaterialInfo = useCallback(
    (materialCode: string) => {
      return materialDataMap[materialCode] || null;
    },
    [materialDataMap],
  );

  // 处理搜索变化
  const handleMaterialSearch = useCallback(
    async (searchValue: string) => {
      if (searchValue) {
        await fetchMaterialData(searchValue);
      } else {
        // 搜索为空时，清空选项或加载初始数据
        setMaterialOptions([]);
      }
    },
    [fetchMaterialData],
  );

  return {
    materialOptions,
    materialDataMap,
    getMaterialInfo,
    loading,
    fetchMaterialData,
    handleMaterialSearch,
  };
};
